// Extract review stats from a hydrated Yotpo bottomLine widget.
// Yotpo on this tenant emits "198 Reviews" (no rating, no parentheses).
// Mirrors the card post-processing in main-collection-horizontal-pills.liquid
// so the shared drawer can re-apply stats after re-hydration.
function qaExtractReviewStats(widget) {
  const text = (widget.textContent || '').trim();
  if (!text) return null;
  const countMatch =
    text.match(/(\d[\d,]*)\s*reviews?\b/i) || text.match(/\((\d[\d,]*)\)/);
  const ratingMatch =
    text.match(/\b(\d(?:\.\d+)?)\s*(?:out\s*of|\/|\s+\()/i) ||
    text.match(/^(\d\.\d+)\b/);
  if (!countMatch) return null;
  return {
    count: countMatch[1].replace(/,/g, ''),
    rating: ratingMatch ? ratingMatch[1] : null,
  };
}

function qaApplyReviewStats(widget) {
  const stats = qaExtractReviewStats(widget);
  if (!stats) return false;
  const wrapper = widget.parentElement;
  if (!wrapper || !wrapper.classList.contains('qa-review')) return false;
  const el = wrapper.querySelector('[data-review-summary-display]');
  if (el) {
    el.textContent =
      (stats.rating ? stats.rating + ' ' : '') + '(' + stats.count + ' reviews)';
  }
  return true;
}

class ProductQuickAddDrawer extends HTMLElement {
  constructor() {
    super();
    // Stable refs (the drawer shell never changes; only its contents do).
    this.dialog = this.querySelector('.qa-drawer');
    this.overlay = this.querySelector('[data-qa-overlay]');
    this.closeBtn = this.querySelector('[data-qa-close]');
    this.variantInput = this.querySelector('[data-qa-variant-input]');
    this.totalEl = this.querySelector('[data-qa-total]');
    this.ctaTextEl = this.querySelector('[data-qa-cta-text]');
    this.addBtn = this.querySelector('.qa-add');
    this.imageEl = this.querySelector('[data-qa-image]');
    this.bottleEl = this.querySelector('[data-qa-bottle]');
    this.bottleCodeEl = this.querySelector('[data-qa-bottle-code]');
    this.nameEl = this.querySelector('[data-qa-name]');
    this.notesEl = this.querySelector('[data-qa-notes]');
    this.sizeLabelEl = this.querySelector('[data-qa-size-label]');
    this.sizesEl = this.querySelector('[data-qa-sizes]');
    this.yotpoEl = this.querySelector('[data-qa-yotpo]');
    this.reviewDisplayEl = this.querySelector('[data-review-summary-display]');
    this.productForm = this.querySelector('product-form');

    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());

    // Size selection via delegation, so cloning new size labels into
    // .qa-sizes on populate() doesn't require re-attaching listeners.
    this.sizesEl?.addEventListener('change', (e) => {
      const radio = e.target.closest('[data-qa-size-radio]');
      if (radio) this.onSizeChange(radio);
    });

    this._onKeydown = (e) => {
      if (e.key === 'Escape' && !this.hasAttribute('hidden')) this.close();
    };
    document.addEventListener('keydown', this._onKeydown);

    // Close drawer shortly after form submit so the cart-drawer (opened by
    // product-form.js after /cart/add responds) doesn't overlap visually.
    this.querySelector('form')?.addEventListener('submit', () => {
      setTimeout(() => this.close(), 300);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    this._yotpoObs?.disconnect();
  }

  // Fill the shared drawer from the clicked card's data-* + size template.
  populate(card) {
    const d = card.dataset;

    // Image vs. bottle fallback.
    if (d.productImage && this.imageEl) {
      this.imageEl.src = d.productImage;
      this.imageEl.alt = d.productImageAlt || '';
      this.imageEl.hidden = false;
      if (this.bottleEl) this.bottleEl.hidden = true;
    } else {
      if (this.bottleCodeEl) {
        this.bottleCodeEl.textContent = d.fragranceCode || d.productName || '';
      }
      if (this.bottleEl) this.bottleEl.hidden = false;
      if (this.imageEl) {
        this.imageEl.hidden = true;
        this.imageEl.removeAttribute('src');
      }
    }

    // Name / notes.
    if (this.nameEl) this.nameEl.textContent = d.productName || '';
    if (this.notesEl) {
      if (d.productNotes) {
        this.notesEl.textContent = d.productNotes;
        this.notesEl.hidden = false;
      } else {
        this.notesEl.textContent = '';
        this.notesEl.hidden = true;
      }
    }
    this.dialog?.setAttribute(
      'aria-label',
      'Add ' + (d.productName || '') + ' to bag'
    );

    // Sizes — clone the card's pre-rendered <template>.
    const tpl = card.querySelector('[data-qa-sizes]');
    const hasSizes = d.hasSizes === 'true' && tpl;
    if (this.sizesEl) {
      this.sizesEl.innerHTML = '';
      if (hasSizes) {
        const sizesFragment = tpl.content.cloneNode(true);
        // ENG-8073 — the template's own content is unreachable from applyWithin(document), so
        // the clone is priced here, before it is in the tree. Correct by construction: no
        // flash, and no dependency on the MutationObserver still being connected.
        if (window.nwPricingApplyWithin) window.nwPricingApplyWithin(sizesFragment);
        this.sizesEl.appendChild(sizesFragment);
        this.sizesEl.hidden = false;
      } else {
        this.sizesEl.hidden = true;
      }
    }
    if (this.sizeLabelEl) this.sizeLabelEl.hidden = !hasSizes;

    // Form state.
    const available = d.defaultAvailable === 'true';
    if (this.variantInput) this.variantInput.value = d.defaultVariantId || '';
    if (this.totalEl) {
      this.totalEl.textContent = available ? d.defaultVariantPrice || '' : '';
    }
    if (this.ctaTextEl) {
      this.ctaTextEl.textContent = available ? 'Add to Bag — ' : 'Sold out';
    }
    if (this.addBtn) {
      if (available) this.addBtn.removeAttribute('disabled');
      else this.addBtn.setAttribute('disabled', '');
    }
    this.productForm?.reset?.();

    // Yotpo re-hydration.
    this.populateYotpo(card);
  }

  populateYotpo(card) {
    const d = card.dataset;
    // Pre-fill summary from SSR review data to avoid a blank flash.
    if (this.reviewDisplayEl) {
      if (d.reviewCount && Number(d.reviewCount) > 0) {
        this.reviewDisplayEl.textContent =
          (d.reviewRating ? d.reviewRating + ' ' : '') +
          '(' + d.reviewCount + ' reviews)';
      } else {
        this.reviewDisplayEl.textContent = '';
      }
    }
    if (!this.yotpoEl) return;

    this.yotpoEl.dataset.productId = d.productId || '';
    this.yotpoEl.innerHTML = '';
    this.refreshYotpo();

    // Re-apply our formatting once Yotpo re-renders the widget.
    this._yotpoObs?.disconnect();
    this._yotpoObs = new MutationObserver(() => {
      if (qaApplyReviewStats(this.yotpoEl)) this._yotpoObs.disconnect();
    });
    this._yotpoObs.observe(this.yotpoEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  // Force Yotpo to re-render all bottomLine widgets (the only refresh API
  // used in this theme). Polls until Yotpo init,
  // but caps retries (~10s) so a blocked/slow tenant that never initialises
  // doesn't self-reschedule forever. The SSR summary pre-fill already covers
  // the visual fallback, so giving up here is graceful.
  refreshYotpo(attempt = 0) {
    if (window.yotpo && window.Yotpo && window.yotpo.initialized) {
      try {
        new window.Yotpo.API(window.yotpo).refreshWidgets();
      } catch (e) {
        console.error('Yotpo error:', e);
      }
    } else if (attempt < 20) {
      setTimeout(() => this.refreshYotpo(attempt + 1), 500);
    }
  }

  open(opener) {
    this._opener = opener || document.activeElement;
    this.removeAttribute('hidden');
    const dialog = this.querySelector('.qa-drawer');
    requestAnimationFrame(() => {
      this.querySelector('.qa-overlay')?.classList.add('open');
      dialog?.classList.add('open');
    });
    dialog?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') {
      trapFocus(this, this.closeBtn);
    } else {
      this.closeBtn?.focus();
    }
  }

  close() {
    const dialog = this.querySelector('.qa-drawer');
    this.querySelector('.qa-overlay')?.classList.remove('open');
    dialog?.classList.remove('open');
    dialog?.setAttribute('aria-hidden', 'true');
    setTimeout(() => this.setAttribute('hidden', ''), 300);
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this._opener);
  }

  onSizeChange(radio) {
    if (this.variantInput) this.variantInput.value = radio.value;
    const price = radio.dataset.variantPrice;
    if (this.totalEl && price) this.totalEl.textContent = price;
    this.sizesEl
      ?.querySelectorAll('.qa-size')
      .forEach((l) => l.classList.remove('selected'));
    radio.closest('.qa-size')?.classList.add('selected');
  }
}

if (!customElements.get('product-quick-add-drawer')) {
  customElements.define('product-quick-add-drawer', ProductQuickAddDrawer);
}

// Bind Quick Add buttons from product cards. Two drawer modes coexist:
//  1) Legacy per-product drawers (homepage-2026): the button's
//     data-quick-add-target resolves to a dedicated <product-quick-add-drawer>.
//  2) Shared drawer (collection grid, ENG-7497): no per-product drawer exists,
//     so we populate the single #product-quick-add-drawer-shared from the card.
//
// Only cancel the click once a drawer is actually going to open. `.pc-atc` and
// `.product-card` are styling classes that non-product markup also reuses (the
// `product_card` editorial tile, ENG-7595, is one such card wrapped in a plain
// link), so an unconditional preventDefault() here would swallow their
// navigation. A real product card always carries data-product-id — that is the
// invariant we key on, not the class alone.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-quick-add-target], .pc-atc');
  if (!btn) return;

  const targetId = btn.dataset.quickAddTarget;
  const dedicated = targetId ? document.getElementById(targetId) : null;
  if (dedicated && typeof dedicated.open === 'function') {
    e.preventDefault();
    dedicated.open(btn);
    return;
  }

  const card = btn.closest('.product-card[data-product-id]');
  const shared = document.getElementById('product-quick-add-drawer-shared');
  if (card && shared && typeof shared.populate === 'function') {
    e.preventDefault();
    shared.populate(card);
    shared.open(btn);
  }
});
